import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import Button from "components/button/Button";
import CircleNumber from "components/products/CircleWithNumber";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import { BarOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepIndex: number;
    menuExpanded: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
}

const ReimageTargetMachine = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [showContent, setShowContent] = React.useState<boolean>(false);
    const stepName = BarOnboardingSteps.STEP_REIMAGE_TARGET_MACHINE;

    const clickEvent = () => {
        props.stepClickable && setShowContent(!showContent);
    };

    const saveOnboardingStep = () => {
        props.saveOnboardingStep({
            product: OnboardingProductType.BAR,
            step: BarOnboardingSteps.STEP_REIMAGE_TARGET_MACHINE,
        });
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const steps = [
        {
            description: t("Onboarding.barHomePage.reimageTargetMachine.steps.step1"),
        },
        {
            description: t("Onboarding.barHomePage.reimageTargetMachine.steps.step2"),
        },
        {
            description: t("Onboarding.barHomePage.reimageTargetMachine.steps.step3"),
        },
        {
            description: t("Onboarding.barHomePage.reimageTargetMachine.steps.step4"),
        },
        {
            description: t("Onboarding.barHomePage.reimageTargetMachine.steps.step5"),
        },
        {
            description: t("Onboarding.barHomePage.reimageTargetMachine.steps.step6"),
        },
        {
            description: t("Onboarding.barHomePage.reimageTargetMachine.steps.step7"),
        },
        {
            description: t("Onboarding.barHomePage.reimageTargetMachine.steps.step8"),
        },
    ];

    function createContent() {
        return (
            <>
                <div className={style.stepsContainer}>
                    <div className={style.bannerText}>
                        {t("Onboarding.barHomePage.reimageTargetMachine.description")}
                    </div>
                    {steps.map((step, index) => (
                        <div key={index} className={style.subStepsToFollowLayout}>
                            <CircleNumber number={index + 1} subStep={true} />
                            <div className={style.stepPadding}>
                                <div>{step.description}</div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <div className={style.templateSelectedBlock}>
                            <StepCompletedText
                                successText={t("Onboarding.gettingStartedFlow.completed")}
                                testId={testIds.workArea.onboarding.step.statusLabel}
                                dataType={OnboardingProductType.BAR + "_" + stepName}
                            />
                        </div>
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={() => {
                                props.saveStepInProgressIndex(props.stepIndex);
                                saveOnboardingStep();
                            }}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={OnboardingProductType.BAR + "_" + stepName}
                        >
                            {props.stepInProgressIndex === props.stepIndex
                                ? t("Onboarding.saving")
                                : t("Onboarding.barHomePage.reimageTargetMachine.okButton")}
                        </Button>
                    )}
                </div>
            </>
        );
    }

    return (
        <StepHeader
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            title={t("Onboarding.barHomePage.reimageTargetMachine.title")}
            stepIndex={props.stepIndex}
            menuExpanded={props.menuExpanded}
            showContent={showContent}
            clickEvent={clickEvent}
            content={createContent()}
            type={OnboardingProductType.BAR}
            stepName={stepName}
        />
    );
};

export default connector(ReimageTargetMachine);
